<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { FontIcon } from '../FontIcon';
  import {
    TagColor,
    TagVariant,
    type TagColorType,
    type TagProps,
  } from './Tag.types';
  import {
    baseClass,
    colorClasses,
    displayClass,
    iconClass,
    iconContainerClass,
    iconTagClass,
    iconTagGlyphClass,
    labelClass,
    removeButtonClass,
    removeButtonIconClass,
    selectionClass,
  } from './Tag.css';

  const props = defineProps<TagProps>();

  const emit = defineEmits<{
    remove: [];
  }>();

  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-tag-${uid}`);

  const isIcon = computed(() => props.variant === TagVariant.ICON);
  const isSelection = computed(() => props.variant === TagVariant.SELECTION);

  const resolvedColor = computed<TagColorType>(() =>
    'color' in props && props.color ? props.color : TagColor.GREY,
  );

  const resolvedIconName = computed<TUNDR_ICONS | undefined>(() =>
    'iconName' in props ? props.iconName : undefined,
  );

  const classes = computed(() => {
    if (isIcon.value) {
      return [iconTagClass, colorClasses[resolvedColor.value]];
    }

    if (isSelection.value) {
      return [baseClass, selectionClass];
    }

    return [baseClass, displayClass, colorClasses[resolvedColor.value]];
  });

  const onRemove = () => {
    emit('remove');
  };
</script>

<template>
  <div
    :class="classes"
    :data-testid="resolvedTestId"
    :aria-hidden="isIcon ? 'true' : undefined"
  >
    <FontIcon
      v-if="isIcon && resolvedIconName"
      :icon-name="resolvedIconName"
      :class="iconTagGlyphClass"
    />

    <template v-else>
      <span
        v-if="!isSelection && resolvedIconName"
        :class="iconContainerClass"
        aria-hidden="true"
      >
        <FontIcon :icon-name="resolvedIconName" :class="iconClass" />
      </span>
      <span :class="labelClass"> <slot /> </span>
      <button
        v-if="isSelection"
        :class="removeButtonClass"
        type="button"
        :aria-label="removeAriaLabel ?? t('tag.remove_aria_label')"
        @click.stop="onRemove"
      >
        <FontIcon
          :icon-name="TUNDR_ICONS.CLOSE"
          :class="removeButtonIconClass"
        />
      </button>
    </template>
  </div>
</template>
